import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Input, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { doRequest } from "../utils/helper";
import Loader from "../components/Loader";
import Sidebar from "../components/Sidebar";
import SearchHeader from "../components/SearchHeader";
import AdminFooter from "../components/AdminFooter";
import RcPagination from "rc-pagination";
import localeInfo from "rc-pagination/lib/locale/en_US";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Add some custom styles for the user info cell and file preview
const userInfoStyles = {
  userInfoCell: {
    minWidth: '200px',
    maxWidth: '250px'
  },
  userName: {
    fontWeight: 'bold',
    color: '#007bff',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  userEmail: {
    fontSize: '12px',
    color: '#6c757d',
    marginTop: '2px'
  },
  userMobile: {
    fontSize: '12px',
    color: '#6c757d',
    marginTop: '2px'
  }
};

const filePreviewStyles = {
  previewCell: {
    minWidth: '300px',
    maxWidth: '400px'
  },
  fileThumbnail: {
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'scale(1.05)'
    }
  }
};

export default function UserFilesList() {
  const [filesList, setFilesList] = useState([]);
  const [filteredFilesList, setFilteredFilesList] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterUserId, setFilterUserId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [userRole, setUserRole] = useState("");
  
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const userLoginDetails = localStorage.getItem("USER_LOGIN_DETAILS");
    if (userLoginDetails) {
      const user = JSON.parse(userLoginDetails);
      setUserRole(user.type || "");
    }
  }, []);

  const textItemRender = (current, type, element) => {
    if (type === "prev") {
      return (
        <FontAwesomeIcon
          icon={["fas", "chevron-left"]}
          className="font-size-sm"
        />
      );
    }
    if (type === "next") {
      return (
        <FontAwesomeIcon
          icon={["fas", "chevron-right"]}
          className="font-size-sm"
        />
      );
    }
    return element;
  };

  const getFilesList = useCallback(
    async (page = 1, limit = 10, userId = null) => {
      try {
        setLoading(true);
        let url = `file/user-files-list?page=${page}&limit=${limit}`;
        
        // Add user filter for admin users
        if (userRole === "admin" && userId) {
          url += `&filter_user_id=${userId}`;
        }

        const response = await doRequest("", "GET", url);
        
        if (response?.message === "User images retrieved successfully") {
          setFilesList(response.data.images || []);
          setFilteredFilesList(response.data.images || []);
          setTotalRecords(response.data.totalItems || 0);
        } else {
          alert(response?.message || "Something went wrong!");
          setFilesList([]);
          setFilteredFilesList([]);
          setTotalRecords(0);
        }
      } catch (error) {
        console.error("Error fetching files:", error);
        alert("Failed to fetch files. Please try again.");
        setFilesList([]);
        setFilteredFilesList([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [userRole]
  );

  // Search functionality
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredFilesList(filesList);
      return;
    }

    const searchTerm = query.toLowerCase().trim();
    const filtered = filesList.filter((file) => {
      const fileName = (file.file_name || "").toLowerCase();
      const mimeType = (file.mime_type || "").toLowerCase();
      const userName = (file.user_name || "").toLowerCase();
      const userEmail = (file.user_email || "").toLowerCase();
      const userMobile = (file.mobile || "").toLowerCase();
      
      return fileName.includes(searchTerm) || 
             mimeType.includes(searchTerm) ||
             userName.includes(searchTerm) ||
             userEmail.includes(searchTerm) ||
             userMobile.includes(searchTerm);
    });
    
    setFilteredFilesList(filtered);
  }, [filesList]);

  // Handle user filter change
  const handleUserFilterChange = (e) => {
    const userId = e.target.value;
    setFilterUserId(userId);
    setCurrentPage(1);
    getFilesList(1, itemsPerPage, userId);
  };

  // Clear user filter
  const clearUserFilter = () => {
    setFilterUserId("");
    setCurrentPage(1);
    getFilesList(1, itemsPerPage, null);
  };

  // Handle pagination
  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setItemsPerPage(pageSize);
    getFilesList(page, pageSize, filterUserId || null);
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return '🖼️';
    if (mimeType === 'application/pdf') return '📄';
    if (mimeType.includes('video/')) return '🎥';
    if (mimeType.includes('audio/')) return '🎵';
    if (mimeType.includes('text/')) return '📝';
    if (mimeType.includes('application/')) return '📋';
    return '📁';
  };

  // Download file
  const downloadFile = (file) => {
    const link = document.createElement('a');
    link.href = file.file_url;
    link.download = file.file_name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // View file
  const viewFile = (file) => {
    window.open(file.file_url, '_blank');
  };

  useEffect(() => {
    getFilesList(currentPage, itemsPerPage, filterUserId || null);
  }, [getFilesList, currentPage, itemsPerPage, filterUserId]);

  // Update filtered results when filesList changes
  useEffect(() => {
    setFilteredFilesList(filesList);
  }, [filesList]);

  return (
    <>
      {loading && <Loader />}
      <div className="container-fluid main">
        <Sidebar />
        <section className="home">
          <div className="container-fluid">
            <div className="searcmain_pad">
              <div className="search_header">
                <ul className="search_list">
                  <li>
                    <h3>User Files</h3>
                  </li>
                  <li>
                    {/* <div className="cansear_div">
                      <button className="can_sea" onClick={() => navigate(-1)}>
                        Back
                      </button>
                    </div> */}
                  </li>
                </ul>
              </div>

              {/* Admin Filter Controls */}
              {userRole === "admin" && (
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="admin-controls">
                      <Input
                        type="number"
                        placeholder="Filter by User ID"
                        value={filterUserId}
                        onChange={handleUserFilterChange}
                        className="filter-input"
                      />
                      <Button
                        color="secondary"
                        size="sm"
                        onClick={clearUserFilter}
                        className="ms-2"
                      >
                        Clear Filter
                      </Button>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Search Header */}
              {/* <SearchHeader
                placeHolder="Search by file name or type..."
                search={searchQuery}
                onChangeHandler={handleSearch}
              /> */}

              {/* Search Results Info */}
              {searchQuery && (
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="search-results-info">
                    <span className="text-muted">
                      Found {filteredFilesList.length} files matching "{searchQuery}"
                    </span>
                  </div>
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSearchQuery("");
                      setFilteredFilesList(filesList);
                    }}
                  >
                    Clear Search
                  </Button>
                </div>
              )}

              {/* Files Table */}
              <div className="table-responsive table_user_mar">
                <Table hover striped className="table_cust align-middle">
                  <thead className="thead_bg">
                    <tr>
                      {/* <th className="userth">Icon</th> */}
                      <th className="userth">File ID</th>
                      <th className="userth">File Info</th>
                      <th className="userth">Type</th>
                      <th className="userth">Size</th>
                      <th className="userth">Upload Date</th>
                      <th className="userth">User Info</th>
                      {/* <th className="userth">Actions</th> */}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFilesList?.length > 0 ? (
                      filteredFilesList.map((file, index) => (
                        <tr key={file.id || index}>
                          {/* <td>
                            <span className="file-icon">
                              {getFileIcon(file.mime_type)}
                            </span>
                          </td> */}
                          <td>
                            <div className="file-name-cell">
                              <strong>{file.id}</strong>
                            </div>
                          </td>
                          <td style={filePreviewStyles.previewCell}>
                            <div className="file-preview-cell d-flex align-items-center">
                              <div className="file-preview me-3">
                                <img
                                  src={file.file_url || '/assets/images/placeholder-image.png'}
                                  alt={file.file_name}
                                  className="file-thumbnail"
                                  onError={(e) => {
                                    e.target.src = '/assets/images/placeholder-image.png';
                                  }}
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'cover',
                                    borderRadius: '6px',
                                    border: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = 'scale(1.05)';
                                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = 'scale(1)';
                                    e.target.style.boxShadow = 'none';
                                  }}
                                  onClick={() => viewFile(file)}
                                  title="Click to view full image"
                                />
                              </div>
                              <div className="file-name-details">
                                <div className="file-name">
                                  <strong>{file.file_name}</strong>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="file-type">
                              {file.mime_type}
                            </span>
                          </td>
                          <td>
                            <span className="file-size">
                              {file.file_size_mb}
                            </span>
                          </td>
                          <td>
                            <span className="file-date">
                              {new Date(file.created_at).toLocaleDateString()}
                            </span>
                          </td>
                          <td style={userInfoStyles.userInfoCell}>
                            <div className="user-info-cell">
                              <div className="user-name">
                                <Button
                                  color="link"
                                  className="p-0 text-decoration-none"
                                  onClick={() => window.open(`/viewprofile/${file.user_id}`, "_blank")}
                                  style={userInfoStyles.userName}
                                >
                                  {file.user_name || 'N/A'}
                                </Button>
                              </div>
                              <div className="user-email" style={userInfoStyles.userEmail}>
                                {file.user_email || 'N/A'}
                              </div>
                              <div className="user-mobile" style={userInfoStyles.userMobile}>
                                {file.mobile || 'N/A'}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          {searchQuery 
                            ? "No files found matching your search criteria." 
                            : "No files found."
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredFilesList.length > 0 && !searchQuery && (
                <div className="mt-3 d-flex justify-content-center">
                  <RcPagination
                    showSizeChanger
                    defaultPageSize={itemsPerPage}
                    current={currentPage}
                    total={totalRecords}
                    locale={localeInfo}
                    itemRender={textItemRender}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageChange}
                    pageSizeOptions={['10', '20', '50', '100']}
                  />
                </div>
              )}

              {/* Summary Info */}
              <div className="mt-3 text-center text-muted">
                <small>
                  Showing {filteredFilesList.length} of {totalRecords} files
                  {userRole === "admin" && filterUserId && ` for User ID: ${filterUserId}`}
                </small>
              </div>
            </div>
          </div>
        </section>
      </div>
      <AdminFooter />
    </>
  );
}

